import { useEffect, useState } from "react";
import { X, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import InputRupiah from "../InputRupiah";
import DaftarBarisPembayaran from "./DaftarBarisPembayaran";
import { nilaiTotalKasir } from "../../lib/payloadNota";

/* ============================================================================
 * Jendela approve draf nota — Total Pembayaran (batch N3, brief owner 22 Sep
 * 2026 D6): "Total harus diisi oleh kasir untuk memastikan total uang yang
 * diterimanya sesuai dengan total yang sudah diinput di sistem. Saat diisi dan
 * nominal tidak sesuai, maka tidak dapat diproses dan ada alert-nya."
 *
 * SEMUA tombol approve (baris daftar desktop & ponsel, detail nota) membuka
 * jendela ini — approve satu-klik tanpa angka sudah tidak ada. Yang
 * ditampilkan: baris pembayaran dari pratinjau server (DP segar, akun
 * penampung trade-in, peringatan) BESERTA nominal tiap baris — leasing &
 * transfer memang harus terlihat karena uangnya tidak ada di laci. Tidak ada
 * baris "total", tetapi untuk nota satu baris angkanya tetap terbaca: dalam
 * bentuk sekarang Total Pembayaran adalah KONFIRMASI yang dicatat (percobaan
 * salah masuk `konfirmasi_gagal`, pesan 400 menyebut angka yang benar), bukan
 * hitungan buta uang fisik — keputusannya menunggu Bos (tinjauan 1 N3).
 *
 * Kolom memakai `InputRupiah bolehKosong`: kosong dikirim `null` (ditolak
 * "Isi Total Pembayaran"), angka 0 yang diketik tetap 0 — sah untuk nota
 * bertempo tanpa uang muka atau nota downgrade. Tanpa itu kolom yang tidak
 * disentuh terkirim 0 dan lolos untuk nota yang memang bernilai 0.
 *
 * Draf LAMA bertempo + trade-in (tinjauan gelombang 3 putaran 2): server menahan
 * approve-nya (409). Pratinjau membawa vonis yang SAMA (`tempo_tradein`, fungsi
 * `vonis_tempo_tradein_klien_lama`) — tombol Setujui mati dengan alasan tertulis
 * dan jalan keluar "Buka lewat Edit" (`onEdit`), bukan klik yang berujung 409.
 * ========================================================================== */
export default function KonfirmasiTotalPembayaran({ nota, onClose, onKirim, onEdit }) {
  const [data, setData] = useState(null);
  const [gagalMuat, setGagalMuat] = useState("");
  const [nilai, setNilai] = useState("");
  const [galat, setGalat] = useState("");
  const [mengirim, setMengirim] = useState(false);

  useEffect(() => {
    let batal = false;
    api.get(`/nota/sales/${nota.id}/rincian-bayar`)
      .then(r => {
        if (batal) return;
        setData(r.data);
        const tersimpan = r.data?.total_pembayaran_tersimpan;
        if (tersimpan !== null && tersimpan !== undefined) setNilai(tersimpan);
      })
      .catch(e => { if (!batal) setGagalMuat(e?.response?.data?.detail || "Gagal memuat rincian pembayaran"); });
    return () => { batal = true; };
  }, [nota.id]);

  const kirim = async () => {
    setGalat("");
    setMengirim(true);
    try {
      await onKirim(nilaiTotalKasir(nilai));
    } catch (e) {
      setGalat(e?.response?.data?.detail || "Gagal menyetujui nota");
    } finally {
      setMengirim(false);
    }
  };

  const baris = data?.baris || [];
  // Dibaca dari server, tidak ditebak dari isi draf (aturan 3 CLAUDE.md).
  const ditahanT2 = data?.tempo_tradein?.ditahan === true;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid="approve-modal">
        <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
          <h3 className="font-semibold text-sm">Setujui Nota — {nota.nama_pelanggan}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded" data-testid="approve-tutup">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {gagalMuat && <p className="text-xs text-rose-700" data-testid="approve-gagal-muat">{gagalMuat}</p>}
          {!data && !gagalMuat && (
            <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat rincian pembayaran…
            </p>
          )}
          {data && (
            <>
              <div className="space-y-1" data-testid="approve-baris">
                <DaftarBarisPembayaran baris={baris} testidPrefix="approve-baris"
                  kosong={<p className="text-xs text-slate-500">Tidak ada pembayaran di kasir untuk nota ini.</p>} />
              </div>
              {data.akun_tradein && (
                <p className="text-[11px] text-violet-800" data-testid="approve-akun-tradein">
                  Nilai trade-in dicatat di {data.akun_tradein.kode} {data.akun_tradein.nama || ""} (modal
                  kerja, bukan kas) dan dipakai saat gudang menerima barangnya lewat PO Barang Trade-in.
                </p>
              )}
              {(data.peringatan || []).map((p, i) => (
                <p key={i} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                  data-testid={`approve-peringatan-${i}`}>
                  <AlertTriangle className="inline w-3 h-3 mr-1" />{p}
                </p>
              ))}
              {ditahanT2 && (
                <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 space-y-1.5"
                  role="alert" data-testid="approve-ditahan-tempo-tradein">
                  <p><AlertTriangle className="inline w-3 h-3 mr-1" />{data.tempo_tradein.alasan}</p>
                  {onEdit && (
                    <button type="button" onClick={onEdit} data-testid="approve-buka-edit"
                      className="h-8 px-3 rounded-lg border border-rose-300 bg-white hover:bg-rose-100 text-xs font-medium text-rose-800">
                      Buka lewat Edit
                    </button>
                  )}
                </div>
              )}
              {!ditahanT2 && (
              <label className="block">
                <span className="text-xs font-medium text-slate-700">
                  Total Pembayaran {data.wajib_total_pembayaran ? "*" : "(draf lama — boleh kosong)"}
                </span>
                <InputRupiah value={nilai} onChange={v => { setNilai(v === null ? "" : v); setGalat(""); }}
                  bolehKosong placeholder="Uang yang Anda terima" testid="approve-total-pembayaran"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 text-right font-mono text-base" />
                <span className="block text-[11px] text-slate-500 mt-1">
                  Jumlah SEMUA pembayaran lewat metode pembayaran (tunai + transfer + kartu/EDC +
                  leasing/cicilan), di luar trade-in, DP, dan Kredit Retur.
                  {data.bertempo ? " Nota bertempo: isi 0 kalau belum ada uang muka." : ""}
                  {data.downgrade ? " Nota downgrade: isi 0 — selisihnya ditransfer Finance." : ""}
                </span>
              </label>
              )}
              {galat && (
                <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" role="alert"
                  data-testid="approve-galat">
                  {galat}
                </p>
              )}
            </>
          )}
        </div>
        <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={kirim} disabled={!data || mengirim || ditahanT2} data-testid="approve-kirim"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> {mengirim ? "Memproses…" : "Setujui & Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}
